import { useCallback, useEffect, useRef, useState } from "react";
import { MIN_PLACE_QUERY, newPlacesSession, placesAvailable, suggestPlaces } from "@/lib/places";

export const PLACE_SEARCH_DEBOUNCE_MS = 250;

const IDLE = { suggestions: [], loading: false, outcome: "idle" };

/**
 * The address lookup behind one field: 250 ms after the last keystroke, at
 * least two characters, never for text the person did not type (`enabled`
 * false: a value loaded from a saved listing, the label of a pick), and only
 * the newest answer counts. One Places session token per typed address;
 * `renewSession` starts the next one after a pick.
 *
 * outcome: "idle" | "results" | "empty" | "unavailable" | "error". The last
 * two never show a list; the field then behaves as plain text.
 */
export default function usePlaceSearch({ query, lang, kind = "address", scope = "near_sn", enabled = true }) {
  const [state, setState] = useState(IDLE);
  const sessionRef = useRef(null);
  if (sessionRef.current === null) sessionRef.current = newPlacesSession();

  useEffect(() => {
    let current = true;
    setState(IDLE);
    const q = typeof query === "string" ? query.trim() : "";
    if (!enabled || q.length < MIN_PLACE_QUERY || !placesAvailable()) return undefined;
    const timer = setTimeout(async () => {
      // Another field on the page may have found Places off in the meantime.
      if (!placesAvailable()) { if (current) setState({ ...IDLE, outcome: "unavailable" }); return; }
      setState({ suggestions: [], loading: true, outcome: "idle" });
      try {
        const result = await suggestPlaces({ q, lang, kind, scope, session: sessionRef.current });
        if (!current) return;
        if (!result.available) { setState({ ...IDLE, outcome: "unavailable" }); return; }
        setState({ suggestions: result.suggestions, loading: false, outcome: result.suggestions.length ? "results" : "empty" });
      } catch (_) {
        if (current) setState({ ...IDLE, outcome: "error" });
      }
    }, PLACE_SEARCH_DEBOUNCE_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [query, lang, kind, scope, enabled]);

  const renewSession = useCallback(() => {
    const used = sessionRef.current;
    sessionRef.current = newPlacesSession();
    return used;
  }, []);
  const clear = useCallback(() => setState(IDLE), []);

  return { ...state, renewSession, clear };
}
